import type { DropdownOption } from '../../../../components/Dropdown/DropdownOption/DropdownOption';
import type { UseAppForm } from '../../../form';

import { useStore } from '@tanstack/react-form';
import { use, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EMPTY_ARRAY,
  EMPTY_FUNCTION,
  EMPTY_OBJECT,
} from '../../../../lib/globals';
import { deepReadKeyValue, getTestProps } from '../../../../lib/helpers';

import Button from '../../../../components/Button/Button';

import UserContext from '../../../../contexts/UserContext';

import { DeleteIcon, WarningTriangleIcon } from '../../../../images/shapes';

import { useFieldContext, useFormContext } from '../../../form';

interface SpaceSelectorItemProps {
  /**
   * Index of the item in the list
   */
  idx: number;
  /**
   * Callback to remove the item
   */
  onRemove?: (idx: number) => void;
  /**
   * Available spaces for selection
   */
  availableSpaces?: DropdownOption[];
  /**
   * All spaces for selection, including those not in availableSpaces
   */
  allspaces?: DropdownOption[];
  /**
   * User roles by space
   */
  userRolesBySpace?: Record<string, DropdownOption[]>;
  /**
   * Whether the item is disabled
   */
  disabled?: boolean;
  /**
   * Test ID for testing purposes
   */
  testId?: string;
}

export type SpaceSelectorItemField = {
  id: string;
  userRoles: { id: string }[];
};

const SpaceSelectorItem = ({
  idx,
  onRemove = EMPTY_FUNCTION,
  availableSpaces = EMPTY_ARRAY,
  allspaces = EMPTY_ARRAY,
  userRolesBySpace = EMPTY_OBJECT,
  disabled = false,
  testId = '',
}: SpaceSelectorItemProps) => {
  const field = useFieldContext<SpaceSelectorItemField>();
  const form = useFormContext() as unknown as UseAppForm;

  const { t } = useTranslation();
  const { isAdmin } = use(UserContext);

  const removeSpace = useCallback(() => {
    onRemove(idx);
  }, [idx, onRemove]);

  const spaceFieldListeners = useMemo(
    () => ({
      onChange: () => {
        form.setFieldValue(`${field.name}.userRoles`, []);
      },
    }),
    [form, field.name],
  );

  const spaceId = useStore(
    form.store,
    ({ values }) => deepReadKeyValue(`${field.name}.id`, values) as string,
  );

  return (
    <div className="flex flex-col items-center md:flex-row md:justify-between md:items-start gap-2">
      <form.AppField name={`${field.name}.id`} listeners={spaceFieldListeners}>
        {(field) => (
          <field.Dropdown
            options={availableSpaces}
            extraOptions={allspaces}
            placeholder={t('Spaces.SpaceSelector.PlaceHolder')}
            emptyOptions={
              <div className="py-2 px-4 dark:text-slate-200">
                {t('Spaces.SpaceSelector.EmptySpaces')}
              </div>
            }
            additionalClasses="w-full"
            disabled={disabled}
            hideSearch
            {...getTestProps(testId, 'space', 'testId')}
          />
        )}
      </form.AppField>

      <form.AppField name={`${field.name}.userRoles`} mode="array">
        {(field) => {
          const rolesLength = (field.state.value as { id: string }[])?.length;
          return (
            <field.Dropdown
              options={userRolesBySpace[spaceId] || []}
              placeholder={
                rolesLength
                  ? t('UserRoles.RoleSelector.EmptyRoles')
                  : t('UserRoles.RoleSelector.PlaceHolder')
              }
              helpText={
                !rolesLength &&
                spaceId &&
                isAdmin && (
                  <div className="text-orange-600! flex items-center ">
                    <WarningTriangleIcon className="w-5 h-5 mr-0 md:mr-2" />
                    <label className="break-normal!	">
                      {t('UserRoles.RoleSelector.NoRoleSelectedWarning')}
                    </label>
                  </div>
                )
              }
              additionalClasses="w-full"
              disabled={!spaceId || disabled}
              multiple
              nullable
              {...getTestProps(testId, 'roles', 'testId')}
            />
          );
        }}
      </form.AppField>

      {spaceId && isAdmin && (
        <Button
          buttonColor="borderless"
          additionalClasses="w-fit h-full px-2 md:py-3.5"
          onClick={removeSpace}
          disabled={disabled}
          {...getTestProps(testId, 'delete', 'testId')}
        >
          <DeleteIcon className={'w-5 h-5'} />
        </Button>
      )}
    </div>
  );
};

export default SpaceSelectorItem;
